import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import {
    ChangeEvent,
    Dispatch,
    FC,
    FormEvent,
    MouseEvent,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import AddPcapFilesDialog from './AddPcapFilesDialog';
import { UseFormResetField, UseFormSetValue } from 'react-hook-form';
import { TrexConfig } from './TrexConfigForm';
import UtmsButton from '@shared/components/ui/UtmsButton';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import PcapFileItem from './PcapFIleItem';
import { PcapFileWithCps } from '../types';
import { downloadPcapFileReset } from '../store';
import { selectDownloadPcapFileError, selectDownloadPcapFileStatus } from '../store/selectors';
import { RequestStatusEnum } from '@shared/types';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useLibraryChunk } from '@pages/library/hooks/useLibraryChunk';

const PcapFilesUsedListDialog = (props: PcapFilesUsedListDialogProps) => (
    <ComponentWithChunks
        Component={PcapFilesUsedListDialogComponent}
        featuresArray={['library']}
        hooksArray={[useLibraryChunk]}
        {...props}
    />
);

interface PcapFilesUsedListDialogProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    setValue: UseFormSetValue<TrexConfig>;
    resetField: UseFormResetField<TrexConfig>;
    pcapFiles: PcapFileWithCps[];
}

const PcapFilesUsedListDialogComponent: FC<PcapFilesUsedListDialogProps> = ({
    open,
    setOpen,
    setValue,
    resetField,
    pcapFiles,
}) => {
    const { t } = useTranslation();
    const [openPcapDialog, setOpenPcapDialog] = useState(false);
    const [pcapFilesUsed, setPcapFilesUsed] = useState<PcapFileWithCps[]>(pcapFiles);
    const dispatch = useDispatch();
    const downloadPcapFileStatus = useSelector(selectDownloadPcapFileStatus);

    const handleOpenAddPcapDialog = () => {
        setOpenPcapDialog(true);
    };

    useEffect(() => {
        if (open) {
            setPcapFilesUsed(pcapFiles);
        }
    }, [open, pcapFiles]);

    const handleClose = () => {
        setOpen(false);
        setPcapFilesUsed(pcapFiles);
        resetField('pcap_files');
    };

    const handleAddPcapFiles = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setOpen(false);
        setValue('pcap_files', pcapFilesUsed);
    };

    const handleChangeCps = useCallback(
        (pk: number) => (e: ChangeEvent<HTMLInputElement>) => {
            setPcapFilesUsed((prev) =>
                prev.map((pcapFile) => (pcapFile.pk === pk ? { ...pcapFile, cps: Number(e.target.value) } : pcapFile)),
            );
        },
        [],
    );

    const handleDeletePcapFile = useCallback(
        (pk: number) => (_e: MouseEvent<HTMLButtonElement>) => {
            setPcapFilesUsed((prev) => prev.filter((pcapFile) => pcapFile.pk !== pk));
        },
        [],
    );

    const handleDownloadPcapFile = useCallback(
        (pk: number) => (_e: MouseEvent<HTMLButtonElement>) => {
            //@ts-ignore
            dispatch(downloadPcapFileRequest(pk));
        },
        [dispatch],
    );

    return (
        <>
            <Dialog
                open={open}
                maxWidth={false}
                PaperProps={{
                    component: 'form',
                    onSubmit: handleAddPcapFiles,
                    sx: { height: 750, width: 600 },
                }}
            >
                <DialogTitle sx={{ fontSize: 20 }}>{t('select') + ' ' + t('pcap_files')}</DialogTitle>
                <DialogContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        maxHeight: 640,
                        overflow: 'auto',
                    }}
                >
                    <Box>
                        <UtmsButton onClick={handleOpenAddPcapDialog} startIcon={<AddIcon />}>
                            {t('add') + ' ' + t('pcap_files')}
                        </UtmsButton>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {pcapFilesUsed.map((pcapFile) => (
                            <PcapFileItem
                                key={pcapFile.pk}
                                pcapFile={pcapFile}
                                handleChangeCps={handleChangeCps}
                                handleDeletePcapFile={handleDeletePcapFile}
                                handleDownloadPcapFile={handleDownloadPcapFile}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <UtmsButton type="submit">{t('apply')}</UtmsButton>
                    <UtmsButton color="secondary" onClick={handleClose}>
                        {t('cancel')}
                    </UtmsButton>
                </DialogActions>
            </Dialog>

            <AddPcapFilesDialog
                open={openPcapDialog}
                setOpen={setOpenPcapDialog}
                pcapFilesUsed={pcapFilesUsed}
                setPcapFilesUsed={setPcapFilesUsed}
            />
            {downloadPcapFileStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('file_r')}
                    action={t('download_p')}
                    resetActionCallback={downloadPcapFileReset}
                    errorSelector={selectDownloadPcapFileError}
                />
            )}
        </>
    );
};

export default PcapFilesUsedListDialog;
